import { useEffect, useState } from 'react';
import { useGlobalContext } from '../../../context/context';
import axios from 'axios';
import { PageSpinner } from '../../../components/spinner/Spinner';

const Investments = () => {
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const { baseURL, userData } = useGlobalContext();

  useEffect(() => {
    const fetchInvestments = async () => {
      try {
        const { data } = await axios.get(`${baseURL}/investments`, {
          headers: { Authorization: `Bearer ${userData?.token}` },
        });
        setInvestments(data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    fetchInvestments();
    // eslint-disable-next-line
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const calculateProgress = (investment) => {
    const now = new Date();
    const start = new Date(investment.startDate);
    const end = new Date(investment.endDate);
    const total = end - start;
    const elapsed = now - start;
    const progress = Math.min(100, Math.max(0, (elapsed / total) * 100));
    return progress.toFixed(1);
  };

  const getDaysRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  if (loading) {
    return (
      <main className='content investments-page'>
        <div className='title'>
          <h2>My Investments</h2>
        </div>
        <PageSpinner />
      </main>
    );
  }

  const activeInvestments = investments.filter((inv) => inv.status === 'active');
  const completedInvestments = investments.filter((inv) => inv.status === 'completed');

  return (
    <main className='content investments-page'>
      <div className='title'>
        <h2>My Investments</h2>
      </div>

      {investments.length === 0 ? (
        <div className='no-data'>
          <h3>No investments yet</h3>
          <p>Start investing by choosing a trading plan</p>
        </div>
      ) : (
        <>
          {activeInvestments.length > 0 && (
            <section className='investments-section'>
              <h3 className='section-title'>Active Investments</h3>
              <div className='investments-grid'>
                {activeInvestments.map((investment) => (
                  <div className='investment-card active' key={investment._id}>
                    <div className='card-header'>
                      <h4>{investment.planTitle}</h4>
                      <span className='status active'>Active</span>
                    </div>

                    <div className='card-body'>
                      <div className='info-row'>
                        <span>Invested</span>
                        <strong>${investment.amount.toLocaleString()}</strong>
                      </div>
                      <div className='info-row'>
                        <span>Expected Profit</span>
                        <strong className='profit'>
                          +${investment.totalExpectedProfit.toLocaleString()}
                        </strong>
                      </div>
                      <div className='info-row'>
                        <span>Profit Accrued</span>
                        <strong className='profit'>
                          +${investment.profitAccrued.toFixed(2)}
                        </strong>
                      </div>
                      <div className='info-row'>
                        <span>Daily Profit</span>
                        <strong>${investment.dailyProfitRate.toFixed(2)}/day</strong>
                      </div>
                      <div className='info-row'>
                        <span>Duration</span>
                        <strong>{investment.duration} days</strong>
                      </div>
                      <div className='info-row'>
                        <span>Days Remaining</span>
                        <strong>{getDaysRemaining(investment.endDate)} days</strong>
                      </div>
                      <div className='info-row'>
                        <span>End Date</span>
                        <strong>{formatDate(investment.endDate)}</strong>
                      </div>

                      <div className='progress-container'>
                        <div className='progress-header'>
                          <span>Progress</span>
                          <span>{calculateProgress(investment)}%</span>
                        </div>
                        <div className='progress-bar'>
                          <div
                            className='progress-fill'
                            style={{ width: `${calculateProgress(investment)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {completedInvestments.length > 0 && (
            <section className='investments-section'>
              <h3 className='section-title'>Completed Investments</h3>
              <div className='investments-grid'>
                {completedInvestments.map((investment) => (
                  <div className='investment-card completed' key={investment._id}>
                    <div className='card-header'>
                      <h4>{investment.planTitle}</h4>
                      <span className='status completed'>Completed</span>
                    </div>

                    <div className='card-body'>
                      <div className='info-row'>
                        <span>Invested</span>
                        <strong>${investment.amount.toLocaleString()}</strong>
                      </div>
                      <div className='info-row'>
                        <span>Total Profit Earned</span>
                        <strong className='profit'>
                          +${investment.profitAccrued.toFixed(2)}
                        </strong>
                      </div>
                      <div className='info-row'>
                        <span>Duration</span>
                        <strong>{investment.duration} days</strong>
                      </div>
                      <div className='info-row'>
                        <span>Completed On</span>
                        <strong>{formatDate(investment.endDate)}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
};

export default Investments;
